<script setup lang="ts" generic="T extends string">
import { cn } from "@voltalis/frontend-utils";
import { useElementSize } from "@vueuse/core";
import { clone, last } from "es-toolkit";
import { computed, useTemplateRef } from "vue";

import { isDefined } from "@/utils/object";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import DropMenu, { IDropMenuItem } from "@/components/ui/menu/drop-menu.vue";

export type TSelectButtonItem<T> = { value: T; label: string };

interface IProps {
  items: TSelectButtonItem<T>[];
}

const props = defineProps<IProps>();

const selectedItem = defineModel<T>({
  required: true,
});

const BUTTON_WIDTH = 200; // in px
const PADDING = 8;
const ELLIPSIS_BUTTON_WIDTH = 40;

const container = useTemplateRef<HTMLDivElement>("container");

const { width: containerWidth } = useElementSize(container);

const visibleItemsNb = computed<number>(() => {
  const buttonsWidth = (BUTTON_WIDTH + PADDING) * props.items.length;

  if (containerWidth.value - ELLIPSIS_BUTTON_WIDTH < buttonsWidth && buttonsWidth < containerWidth.value) {
    return Math.floor(containerWidth.value / (BUTTON_WIDTH + PADDING));
  }

  return Math.floor((containerWidth.value - ELLIPSIS_BUTTON_WIDTH) / (BUTTON_WIDTH + PADDING));
});

const defaultVisibleButtons = computed<TSelectButtonItem<T>[]>(() => {
  return props.items.slice(0, visibleItemsNb.value);
});

const visibleButtons = computed<TSelectButtonItem<T>[]>(() => {
  const selection = props.items.find((b) => b.value === selectedItem.value);

  if (selection && !defaultVisibleButtons.value.includes(selection)) {
    // remove last item from default visible buttons and add selected item to visible buttons
    return defaultVisibleButtons.value.slice(0, defaultVisibleButtons.value.length - 1).concat([selection]);
  }

  return props.items.slice(0, visibleItemsNb.value);
});

const dropMenuItems = computed<IDropMenuItem[]>(() => {
  const defaultHiddenButtons: TSelectButtonItem<T>[] = props.items.slice(visibleItemsNb.value);
  let hiddenButtons: TSelectButtonItem<T>[] = clone(defaultHiddenButtons);

  if (defaultHiddenButtons.find((b) => b.value === selectedItem.value)) {
    const selectedItemIndex = defaultHiddenButtons.findIndex((b) => b.value === selectedItem.value);
    // remove selected item from drop menu items
    hiddenButtons.splice(selectedItemIndex, 1);

    // add last default visible item
    const lastDefaultVisibleItem = last(defaultVisibleButtons.value);
    hiddenButtons = [lastDefaultVisibleItem].concat(hiddenButtons).filter(isDefined);
  }

  return hiddenButtons.map<IDropMenuItem>((item) => {
    return {
      label: item.label,
      callback: () => {
        selectedItem.value = item.value;
      },
    };
  });
});
</script>

<template>
  <div
    ref="container"
    class="select-button flex justify-between items-center"
  >
    <div
      ref="buttons-container"
      class="w-fit flex items-center gap-2"
    >
      <HospitalityButton
        v-for="item in visibleButtons"
        :key="item.label"
        :class="
          cn('!rounded-full mt-2', [
            selectedItem === item.value ? '!outline !outline-offset-2 !outline-brand' : '',
            `!w-[${BUTTON_WIDTH}px]`,
          ])
        "
        severity="secondary"
        size="xs"
        @click="selectedItem = item.value"
      >
        <i :class="cn('text-brand mr-2', `${selectedItem === item.value ? 'pi pi-check' : ''}`)" />
        {{ item.label }}
      </HospitalityButton>
    </div>

    <DropMenu
      v-if="dropMenuItems.length"
      :items="dropMenuItems"
      :position="'left'"
    >
      <HospitalityButton
        ref="ellipsis-button"
        :class="`!w-[${ELLIPSIS_BUTTON_WIDTH}px]`"
        is-icon-only
        severity="secondary"
        size="xs"
      >
        <i class="pi pi-ellipsis-h" />
      </HospitalityButton>
    </DropMenu>
  </div>
</template>
